import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// @material-ui/core components
import { withStyles } from "@mui/styles";
import {
  Card,
  CardBody,
  Button,
  GridContainer,
  GridItem,
} from "~/shared/components/MuiComponents.jsx";

import consultationStyles from "../styles/consultationStyles.js";

import imgSpearmint from "~assets/images/flavors/spearmint-flavor.png";
import imgTangerine from "~assets/images/flavors/tangerine-flavor.png";
import imgFruitPunch from "~assets/images/flavors/tropical-fruit-medley-flavor.png";
import imgSpearmintLegacy from "~assets/images/flavors/legacy/spearmint-flavor.png";
import imgTangerineLegacy from "~assets/images/flavors/legacy/tangerine-flavor.png";
import imgFruitPunchLegacy from "~assets/images/flavors/legacy/tropical-fruit-medley-flavor.png";

import imgOnePack from "~assets/images/visits/one-pack.png";

import {
  clearOrderItemSubcategory,
  getEditPendingVisitOriginalOrderItems,
  getIsEditingPendingVisit,
  getOrderItemSubcategory,
  getVisitID,
  getVisitProduct,
  getVisitUSState,
  isFollowUpVisit,
  setConsultationTypeOrderItem,
  setOrderItemSubcategory,
  setVisitProduct,
} from "~/visits/utils/visitCookies";

import {
  COLOR_KICK_BLUE,
  FRUIT_PUNCH_FLAVOR,
  ORDER_TYPE_PROP_1_PACK,
  ORDER_TYPE_PROP_1_PACK_FLAVORED,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK,
  ORDER_TYPE_PROP_2_PACK_FLAVORED,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
  PRICE_PROP_1_PACK,
  PRICE_PROP_1_PACK_FLAVORED,
  PRICE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
  PRICE_PROP_1_PACK_FOLLOW_UP,
  PRICE_PROP_2_PACK,
  PRICE_PROP_2_PACK_FLAVORED,
  PRICE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
  PRICE_PROP_2_PACK_FOLLOW_UP,
  SPEARMINT_FLAVOR,
  TANGERINE_FLAVOR,
} from "~/shared/sharedConstants.js";

import { supportedUSStatesForCompoundedMeds } from "~/shared/utils/sharedUtils";

import { kickBlue } from "~/shared/styles/baseStyles";
import { useFlavorPhotosExperiment } from "~/shared/experiments/product/flavorPhotos";
import usePendingEditTimeout from "~/visits/utils/usePendingEditTimeout.js";
import {
  FlavorPhoto,
  PRODUCT_PHOTO_SCALE_TABLETS,
} from "~/visits/utils/visitComponents.jsx";

const flavors = [
  {
    name: "Tangerine Citrus Burst",
    image: imgTangerine,
    legacyImage: imgTangerineLegacy,
    imageScale: PRODUCT_PHOTO_SCALE_TABLETS,
    flavorConst: TANGERINE_FLAVOR,
    longDescription:
      "Citrus confidence - The bright, juicy essence of perfectly ripened tangerine in a rapid-dissolve tablet. ",
    priceDiff: 10,
  },
  {
    name: "Tropical Fruit Medley",
    image: imgFruitPunch,
    legacyImage: imgFruitPunchLegacy,
    imageScale: PRODUCT_PHOTO_SCALE_TABLETS,
    longDescription:
      "Beachy, fruit punch-infused flavor in a fast-acting lozenge. Meds without the wince - just refreshing tropical goodness",
    flavorConst: FRUIT_PUNCH_FLAVOR,
    priceDiff: 10,
  },

  {
    name: "Spearmint Chill",
    image: imgSpearmint,
    legacyImage: imgSpearmintLegacy,
    imageScale: PRODUCT_PHOTO_SCALE_TABLETS,
    longDescription:
      "Iceberg calm in a refreshing spearmint quick-dissolve lozenge. Stay minty cool in the hottest moments.",
    flavorConst: SPEARMINT_FLAVOR,
    priceDiff: 10,
  },
  {
    name: "No Thanks",
    image: imgOnePack,
    longDescription: "Regular Tablets",
    flavorConst: "",
    priceDiff: 0,
  },
];
const SelectBBFlavored = ({ classes, history, match }) => {
  const consultationType = match.params.consultationType;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const checkoutSessionID = queryParams.get("checkoutSessionID");
  if (!checkoutSessionID) {
    window.location.href = "/me/portal";
  }

  // Experiment product-flavor-photos: which photo set (and row padding) this
  // checkout renders. Rows wait for `photosReady` so the patient never sees one
  // set swapped for the other.
  const { ready: photosReady, reframed } = useFlavorPhotosExperiment({
    checkoutSessionID,
    consultationType,
  });
  const productRowClass = reframed
    ? classes.selectFlavoredProductWrapperReframed
    : classes.selectFlavoredProductWrapper;

  usePendingEditTimeout(checkoutSessionID);

  const [selectedFlavor, setSelectedFlavor] = useState(() => {
    const existing = getOrderItemSubcategory(
      consultationType,
      checkoutSessionID,
    );
    if (existing) return existing;
    // Editing a pending visit: earlier screens may have wiped the live cookie, so
    // derive the preselection from the original order items — the main product's
    // flavor subcategory, or "" ("No Thanks") when it's a regular (non-flavored) pack.
    if (getIsEditingPendingVisit(checkoutSessionID)) {
      const original = getEditPendingVisitOriginalOrderItems(checkoutSessionID);
      const main = original.find((oi) => !oi.isAddon);
      if (main) return main.subcategory || "";
    }
    return null;
  });
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (!getVisitID(checkoutSessionID)) {
      window.location.href = "/me/portal";
      return;
    }

    const visitIDCheckPoller = setInterval(() => {
      if (!getVisitID(checkoutSessionID)) {
        window.location.href = "/me/portal";
      }
    }, 1000);

    if (!supportedUSStatesForCompoundedMeds.includes(getVisitUSState())) {
      history.replace(
        `/visits/${consultationType}/payment/order-details?checkoutSessionID=${checkoutSessionID}`,
      );
    }

    return () => clearInterval(visitIDCheckPoller);
  }, []);

  const handleSelection = (selectedFlavor) => {
    let modifiedProduct = selectedFlavor;

    setSelectedFlavor(modifiedProduct);
    setShowError(false);
  };

  const setVisitProductAndOrderItem = (productSlug) => {
    setVisitProduct(productSlug, checkoutSessionID);
    setConsultationTypeOrderItem(
      consultationType,
      productSlug,
      checkoutSessionID,
    );
  };

  const nextQuestion = () => {
    if (selectedFlavor === null) {
      setShowError(true);
      return;
    }

    const currentProduct = getVisitProduct(checkoutSessionID) || "";
    const isTwoPack = currentProduct.includes(ORDER_TYPE_PROP_2_PACK);

    // Flavored is selected
    if (selectedFlavor !== "") {
      if (isFollowUpVisit(checkoutSessionID)) {
        setVisitProductAndOrderItem(
          isTwoPack
            ? ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP
            : ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
        );
      } else {
        setVisitProductAndOrderItem(
          isTwoPack
            ? ORDER_TYPE_PROP_2_PACK_FLAVORED
            : ORDER_TYPE_PROP_1_PACK_FLAVORED,
        );
      }
      setOrderItemSubcategory(
        consultationType,
        selectedFlavor,
        checkoutSessionID,
      );
      history.push(
        `/visits/${consultationType}/payment/order-details?checkoutSessionID=${checkoutSessionID}`,
      );
    } else {
      if (isFollowUpVisit(checkoutSessionID)) {
        setVisitProductAndOrderItem(
          isTwoPack
            ? ORDER_TYPE_PROP_2_PACK_FOLLOW_UP
            : ORDER_TYPE_PROP_1_PACK_FOLLOW_UP,
        );
      } else {
        setVisitProductAndOrderItem(
          isTwoPack ? ORDER_TYPE_PROP_2_PACK : ORDER_TYPE_PROP_1_PACK,
        );
      }
      clearOrderItemSubcategory(consultationType, checkoutSessionID);
      history.push(
        `/visits/${consultationType}/try-flavored?checkoutSessionID=${checkoutSessionID}`,
      );
    }

    window.scrollTo(0, 0);
  };

  const isSelected = (product) => {
    return selectedFlavor === product;
  };

  function getPriceDiff(flavorConst) {
    const currentProduct = getVisitProduct(checkoutSessionID) || "";
    const isTwoPack = currentProduct.includes(ORDER_TYPE_PROP_2_PACK);

    if (flavorConst) {
      if (isFollowUpVisit(checkoutSessionID)) {
        return isTwoPack
          ? PRICE_PROP_2_PACK_FLAVORED_FOLLOW_UP - PRICE_PROP_2_PACK_FOLLOW_UP
          : PRICE_PROP_1_PACK_FLAVORED_FOLLOW_UP - PRICE_PROP_1_PACK_FOLLOW_UP;
      } else {
        return isTwoPack
          ? PRICE_PROP_2_PACK_FLAVORED - PRICE_PROP_2_PACK
          : PRICE_PROP_1_PACK_FLAVORED - PRICE_PROP_1_PACK;
      }
    }
  }

  return (
    <div
      className={classes.container}
      style={{ backgroundColor: COLOR_KICK_BLUE }}
    >
      <GridContainer justifyContent="center">
        <GridItem xs={12} sm={10} md={6}>
          <h3
            style={{
              color: "#FFFFFF",
              fontFamily: "Spectral-SemiBold",
              fontSize: "32px",
              marginTop: "40px",
            }}
          >
            Premium Upgrade to Faster, Flavored Beta-Blockers?
          </h3>
          <p
            style={{
              color: "#FFFFFF",
              fontFamily: "BasisGrotesquePro-Regular",
              fontSize: "18px",
              lineHeight: "26px",
            }}
          >
            Only available here, our special compounded beta-blockers come in
            three delicious flavors that work up to 5x faster for those big
            unexpected moments. No need to plan ahead when taking your meds.
            <br />
            <br />
          </p>
        </GridItem>
        <GridItem xs={12} sm={12} md={12}></GridItem>
        <GridItem xs={12} sm={10} md={6}>
          <Card
            className={classes.cardConsultation}
            style={{ paddingTop: "20px", paddingBottom: "20px" }}
          >
            <CardBody
              style={{
                display: "flex",
                flexDirection: "column",
                padding: "0 2px",
              }}
            >
              {!photosReady ? (
                <div
                  role="status"
                  aria-busy="true"
                  aria-label="Loading product options"
                  style={{ minHeight: "160px" }}
                />
              ) : (
                <>
                  {flavors.map((flavor, idx) => {
                    const priceDiff = getPriceDiff(flavor.flavorConst);
                    return (
                      <div key={flavor.flavorConst}>
                        <div
                          className={productRowClass}
                          style={{
                            overflow: "hidden",
                            backgroundColor: isSelected(flavor.flavorConst)
                              ? "#fff7e5"
                              : "",
                          }}
                          onClick={() => handleSelection(flavor.flavorConst)}
                        >
                          <FlavorPhoto
                            reframed={reframed}
                            src={flavor.image}
                            legacySrc={flavor.legacyImage}
                            alt={`${flavor.name} Flavor`}
                            scale={flavor.imageScale}
                          />
                          <div
                            className={classes.selectFlavorImageTitleWrapper}
                          >
                            <h4
                              className={classes.selectFlavorImageTitle}
                              style={{
                                color: kickBlue,
                                fontWeight: "bold",
                              }}
                            >
                              {flavor.name}
                            </h4>
                            <p
                              style={{
                                marginTop: "4px",
                                marginBottom: "8px",
                                fontSize: "16px",
                              }}
                            >
                              {flavor.longDescription}
                            </p>
                            {!!priceDiff && (
                              <p
                                style={{ marginBottom: "0" }}
                              >{`+$${priceDiff}`}</p>
                            )}
                          </div>
                        </div>
                        {idx !== flavors.length - 1 && (
                          <div
                            style={{
                              height: "1px",
                              margin: "0 25%",
                              backgroundColor: "#aaa",
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </CardBody>
          </Card>
          <div style={{ color: "red", margin: "20px auto 6px auto" }}>
            <p style={{ textAlign: "center" }}>
              {showError ? "Please select an option above" : ""}
            </p>
          </div>
          <Button
            onClick={() => {
              nextQuestion();
            }}
            style={{
              display: "block",
              backgroundColor: "#F25930",
              margin: "16px auto",
              width: "300px",
              fontSize: "18px",
              fontWeight: "medium",
              textTransform: "unset",
            }}
          >
            Continue
          </Button>
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <a
              onClick={() => {
                const currentProduct = getVisitProduct(checkoutSessionID) || "";
                const isTwoPack = currentProduct.includes(
                  ORDER_TYPE_PROP_2_PACK,
                );

                if (isFollowUpVisit(checkoutSessionID)) {
                  setVisitProductAndOrderItem(
                    isTwoPack
                      ? ORDER_TYPE_PROP_2_PACK_FOLLOW_UP
                      : ORDER_TYPE_PROP_1_PACK_FOLLOW_UP,
                  );
                } else {
                  setVisitProductAndOrderItem(
                    isTwoPack ? ORDER_TYPE_PROP_2_PACK : ORDER_TYPE_PROP_1_PACK,
                  );
                }
                clearOrderItemSubcategory(consultationType, checkoutSessionID);

                history.push(
                  `/visits/${consultationType}/try-flavored?checkoutSessionID=${checkoutSessionID}`,
                );
              }}
              style={{
                backgroundColor: "transparent",
                color: "white",
                fontSize: "18px",
                fontWeight: "normal",
                textAlign: "center",
                paddingBottom: "20px",
                cursor: "pointer",
              }}
            >
              Skip
            </a>
          </div>
        </GridItem>
      </GridContainer>
    </div>
  );
};

export default withStyles(consultationStyles)(SelectBBFlavored);
